import { FC, useCallback, useEffect, useMemo } from "react";
import {
    AssortmentTaskType,
    Point,
    TaskAssortmentMatrix,
} from "tm-shared/types";

import { ASSORTMENT_SUBTASK_ICON } from "@/constants";
import { useTaskConfigSelector } from "@/domains/tasks/hooks";
import { SVScanButton } from "@/features/SupervisorScan";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { MainContainer } from "@/shared/layout/Layout";
import { setMdTasksConfig } from "@/store/slices/ui";

import { Facings } from "./Facings";
import { FacingsController } from "./Facings/FacingsController";
import { FinGroupPhotos } from "./FinGroupPhotos";
import { FinGroupPhotosController } from "./FinGroupPhotos/components/FinGroupPhotosController";
import { ProductsMatrix } from "./ProductsMatrix";
import { ProductsMatrixController } from "./ProductsMatrix/components/ProductsMatrixController";
import {
    StyledAssortmentMatrixHeader,
    StyledAssortmentMatrixStepCircleIcon,
    StyledAssortmentMatrixSteps,
} from "./Styled";

// Types
export type AMStepStatus = "active" | "finished" | "default";

export interface AMStepData {
    key: string;
    icon: React.ReactNode;
    status: AMStepStatus;
    subtaskType: AssortmentTaskType;
    completed: boolean;
}

interface AssortmentMatrixPageProps {
    point: Point;
    task: TaskAssortmentMatrix;
}

export const AssortmentMatrixPage: FC<AssortmentMatrixPageProps> = ({
    task,
    point,
}) => {
    const dispatch = useAppDispatch();

    const taskId = task.id;
    const pointId = point.id;

    // Selectors
    const { activeItem: activeStepKey } = useTaskConfigSelector({
        pointId,
        taskId,
    });

    const assortmentTaskPayload = useAppSelector(
        (s) => s.progress.assortmentMatrix[taskId],
    );

    const steps = useMemo<AMStepData[]>(() => {
        if (!task.context.subtasks.length) return [];

        const defaultActiveKey =
            activeStepKey ?? task.context.subtasks[0]?.subtaskId;

        return task.context.subtasks.map((subtask) => {
            const isCompleted =
                assortmentTaskPayload?.[subtask.subtaskId]?.completed ?? false;
            const isActive = subtask.subtaskId === defaultActiveKey;
            const Icon = ASSORTMENT_SUBTASK_ICON[subtask.type];

            return {
                key: subtask.subtaskId,
                icon: <Icon style={{ fontSize: 16 }} />,
                status: isActive ? "active" : "default",
                subtaskType: subtask.type as AssortmentTaskType,
                completed: isCompleted,
            };
        });
    }, [task.context.subtasks, assortmentTaskPayload, activeStepKey]);

    const activeStepData = useMemo(
        () => steps.find((step) => step.status === "active"),
        [steps],
    );

    const activeSubtask = useMemo(
        () =>
            activeStepData
                ? task.context.subtasks.find(
                      (subtask) => subtask.subtaskId === activeStepData.key,
                  )
                : null,
        [task.context.subtasks, activeStepData],
    );

    // Handlers
    const handleStepClick = useCallback(
        (stepKey: string) => {
            if (stepKey === activeStepData?.key) return;

            dispatch(
                setMdTasksConfig({
                    pointId,
                    taskId,
                    field: "activeItem",
                    value: stepKey,
                }),
            );
        },
        [activeStepData?.key, dispatch, pointId, taskId],
    );

    const stepperItems = useMemo(
        () =>
            steps.map((step) => ({
                key: step.key,
                icon: (
                    <StyledAssortmentMatrixStepCircleIcon
                        $active={step.status === "active"}
                        $finished={step.completed}
                        onClick={() => handleStepClick(step.key)}
                    >
                        {step.icon}
                    </StyledAssortmentMatrixStepCircleIcon>
                ),
            })),
        [steps, handleStepClick],
    );

    // Initialize active step if not set
    useEffect(() => {
        if (!activeStepKey && steps.length > 0) {
            const firstStepKey = steps[0]?.key;
            if (firstStepKey) {
                dispatch(
                    setMdTasksConfig({
                        pointId,
                        taskId,
                        field: "activeItem",
                        value: firstStepKey,
                    }),
                );
            }
        }
    }, [activeStepKey, steps, dispatch, pointId, taskId]);

    // Render functions
    const renderActiveController = useCallback(() => {
        if (!activeStepData || !activeSubtask) return null;

        switch (activeSubtask.type) {
            case AssortmentTaskType.ProductsMatrix:
                return (
                    <ProductsMatrixController
                        subtask={activeSubtask}
                        point={point}
                        task={task}
                    />
                );
            case AssortmentTaskType.FinGroupPhotos:
                return (
                    <FinGroupPhotosController
                        subtask={activeSubtask}
                        point={point}
                        task={task}
                    />
                );
            case AssortmentTaskType.Facings:
                return (
                    <FacingsController
                        subtask={activeSubtask}
                        point={point}
                        task={task}
                    />
                );
        }
    }, [activeStepData, activeSubtask, point, task]);

    const renderActiveContent = useCallback(() => {
        if (!activeStepData || !activeSubtask) return null;

        switch (activeSubtask.type) {
            case AssortmentTaskType.ProductsMatrix:
                return (
                    <ProductsMatrix
                        subtask={activeSubtask}
                        point={point}
                        task={task}
                    />
                );
            case AssortmentTaskType.FinGroupPhotos:
                return (
                    <FinGroupPhotos
                        subtask={activeSubtask}
                        point={point}
                        task={task}
                    />
                );
            case AssortmentTaskType.Facings:
                return (
                    <Facings
                        subtask={activeSubtask}
                        point={point}
                        task={task}
                    />
                );
        }
    }, [activeStepData, activeSubtask, point, task]);

    return (
        <>
            <Header
                back
                title={`Асортиментна матриця для ${point.market.contragent.name}`}
                rightActions={
                    <>
                        <SVScanButton pointId={pointId} taskId={taskId} />
                    </>
                }
            />

            {!task.context.subtasks.length ? (
                <MainContainer>
                    <div>Немає доступних підзадач</div>
                </MainContainer>
            ) : (
                <>
                    <StyledAssortmentMatrixHeader>
                        <StyledAssortmentMatrixSteps
                            size="small"
                            direction="horizontal"
                            responsive={false}
                            items={stepperItems}
                        />

                        {renderActiveController()}
                    </StyledAssortmentMatrixHeader>

                    {renderActiveContent()}
                </>
            )}
        </>
    );
};
